;(function () {
  const ANIMATION_DURATION = 300
  const DRAG_THRESHOLD = 100

  const drawerOpen = (el) => {
    // There are slightly different designs
    const modifierClasses = (el.dataset.drawerModifierClass || "")
      .split(/\s+/)
      .filter(Boolean)
    const drawer = document.querySelector(".drawer")
    const drawerContent = document.querySelector(".drawer--content")
    const rAF = window.requestAnimationFrame

    const templateId = el.dataset.template
    const templateElement = document.getElementById(templateId)
    if (!templateElement) {
      Bugsnag.notify(`Glossary term template missing: ${templateId}`)
      return
    }

    // drawer drag variables
    let startY
    let currentY
    let activePointerId

    // Overlay drag variables
    let overlayPulling = false
    let overlayStartX, overlayStartY
    let overlayCurrentX, overlayCurrentY
    let overlayActivePointerId

    const open = () => {
      startY = 0
      currentY = 0
      HB.preventScroll()
      drawer.addEventListener("pointerdown", onPointerDown)
      drawerOverlay.addEventListener("pointerdown", onOverlayPointerDown)
      POINTER_START_EVENTS.forEach((name) => {
        drawer.addEventListener(name, stopPropagation)
        drawerOverlay.addEventListener(name, stopPropagation)
      })
      drawerContent.innerHTML = templateElement.innerHTML
      drawer.classList.add(...modifierClasses)
      drawer.classList.add("drawer_opening")
      drawer.classList.remove("hidden")
      updateTouchAction()
      setTimeout(() => {
        drawer.classList.remove("drawer_opening")
      }, ANIMATION_DURATION)
    }

    rAF(open)

    const drawerOverlay = document.querySelector(".drawer--overlay")
    // Prevent document-level outer-click listeners (e.g. search form) from
    // treating pointer-start events on the drawer as "outside" clicks.
    const POINTER_START_EVENTS = ["mousedown", "touchstart"]
    const stopPropagation = (event) => event.stopPropagation()

    drawerOverlay.style.touchAction = "none"

    const threshold = Math.min(DRAG_THRESHOLD, drawer.clientHeight / 2)
    let pulling = false // For drawer content pull-down

    const isContentScrollable = () => drawer.scrollHeight > drawer.clientHeight

    const updateTouchAction = () => {
      drawer.style.touchAction = isContentScrollable() ? "pan-y" : "none"
    }

    const close = (event) => {
      event?.stopPropagation?.()
      // Ensure event listeners are removed
      // Drawer
      drawer.removeEventListener("pointerdown", onPointerDown)
      window.removeEventListener("pointermove", onPointerMove)
      window.removeEventListener("pointerup", onPointerUp)
      window.removeEventListener("pointercancel", onPointerUp)
      // Overlay
      drawerOverlay.removeEventListener("click", close)
      drawerOverlay.removeEventListener("pointerdown", onOverlayPointerDown)
      window.removeEventListener("pointermove", onOverlayPointerMove)
      window.removeEventListener("pointerup", onOverlayPointerUp)
      window.removeEventListener("pointercancel", onOverlayPointerUp)
      POINTER_START_EVENTS.forEach((name) => {
        drawer.removeEventListener(name, stopPropagation)
        drawerOverlay.removeEventListener(name, stopPropagation)
      })

      drawer.classList.add("drawer_closing")
      setTimeout(() => {
        rAF(() => {
          drawer.classList.remove(...modifierClasses)
          drawer.classList.remove("drawer_closing")
          drawer.classList.add("hidden")
          drawerContent.innerHTML = ""
          drawer.removeAttribute("style")
          drawerOverlay.removeAttribute("style")

          HB.releaseScroll()
          drawer.dispatchEvent(
            new CustomEvent("drawer:close", { bubbles: true }),
          )
        })
      }, ANIMATION_DURATION)
    }

    // close drawer on overlay click
    drawerOverlay.addEventListener("click", close)

    // --- Drawer Content Pull-Down Logic ---
    const onPointerDown = (e) => {
      // Prevent starting drawer pull if overlay pull is active
      if (pulling || overlayPulling) return
      // Only trigger on the drawer content/handle, not the overlay itself
      if (e.target === drawerOverlay) return
      // Block pull-to-close when drawer is scrollable
      if (isContentScrollable()) return

      startY = e.clientY
      currentY = 0
      pulling = true

      activePointerId = e.pointerId
      drawer.setPointerCapture(activePointerId)

      drawer.style.transition = ""
      window.addEventListener("pointermove", onPointerMove)
      window.addEventListener("pointerup", onPointerUp)
      window.addEventListener("pointercancel", onPointerUp)
    }

    const onPointerMove = (e) => {
      if (!pulling || e.pointerId !== activePointerId) return
      currentY = e.clientY - startY
      if (currentY < 0) currentY = 0 // don’t allow upward pull

      rAF(() => {
        drawer.style.transform = `translateY(${currentY}px)`
        drawer.style.setProperty("--slide-animation--y", `${currentY}px`)
        drawerOverlay.style.opacity = Math.max(
          1 - Math.min(currentY / threshold, 1),
          0.5,
        )
      })
    }

    const onPointerUp = () => {
      if (!pulling) return
      pulling = false
      window.removeEventListener("pointermove", onPointerMove)
      window.removeEventListener("pointerup", onPointerUp)
      window.removeEventListener("pointercancel", onPointerUp)

      if (currentY > threshold) {
        close()
      } else {
        rAF(() => {
          drawer.style.transition = "transform .3s ease"
          drawer.style.transform = "translateY(0)"
          drawerOverlay.removeAttribute("style")
        })
        const cleanup = () => {
          drawer.style.transition = ""
          drawer.removeEventListener("transitionend", cleanup)
        }
        drawer.addEventListener("transitionend", cleanup)
      }
    }
    // --- End Drawer Content Pull-Down Logic ---

    // --- Drawer Overlay Drag Logic ---
    const onOverlayPointerDown = (e) => {
      // Ignore if already pulling drawer content or overlay
      if (pulling || overlayPulling) return
      // Only act if the direct target is the overlay
      if (e.target !== drawerOverlay) return

      overlayStartX = e.clientX
      overlayStartY = e.clientY
      overlayCurrentX = 0
      overlayCurrentY = 0
      overlayPulling = true
      overlayActivePointerId = e.pointerId

      // Capture pointer on the overlay
      drawerOverlay.setPointerCapture(overlayActivePointerId)

      window.addEventListener("pointermove", onOverlayPointerMove)
      window.addEventListener("pointerup", onOverlayPointerUp)
      window.addEventListener("pointercancel", onOverlayPointerUp)
    }

    const onOverlayPointerMove = (e) => {
      if (!overlayPulling || e.pointerId !== overlayActivePointerId) return

      overlayCurrentX = e.clientX - overlayStartX
      overlayCurrentY = e.clientY - overlayStartY

      const distance = Math.sqrt(
        overlayCurrentX * overlayCurrentX + overlayCurrentY * overlayCurrentY,
      )
      const opacity = Math.max(
        0.5,
        1 - Math.min(distance / DRAG_THRESHOLD, 0.5),
      )
      rAF(() => {
        drawerOverlay.style.opacity = opacity
      })
    }

    const onOverlayPointerUp = (e) => {
      if (!overlayPulling || e.pointerId !== overlayActivePointerId) return

      overlayPulling = false
      rAF(() => {
        drawerOverlay.style.opacity = "" // Reset opacity if changed
      })

      window.removeEventListener("pointermove", onOverlayPointerMove)
      window.removeEventListener("pointerup", onOverlayPointerUp)
      window.removeEventListener("pointercancel", onOverlayPointerUp)

      // Use final coordinates from the event for accuracy
      const finalX = e.clientX - overlayStartX
      const finalY = e.clientY - overlayStartY
      const distance = Math.sqrt(finalX * finalX + finalY * finalY)

      if (distance > DRAG_THRESHOLD) {
        close()
      }
    }
    // --- End Drawer Overlay Drag Logic ---
  }

  document.addEventListener("drawer:open", drawerOpen)
  ;(window.HB = window.HB || {}).drawerOpen = drawerOpen
})()
